import { SharedModule } from '@/app/shared/shared.module';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  EnterpriseProxy,
  InputPriceProxy,
  SubcontractProxy,
  SubcontractUserViewModel,
  UserEnterpriseInfoViewModel,
} from '@/app/proxy/proxies';
import { CsiService } from '../csi/csi.service';
import { finalize, Observable, Subject, takeUntil } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SustainabilityDashboardService } from './sustainability-dashboard.service';
import {
  SustainabilityDashboardTabRoute,
  SustainabilityDashboardStaticAnalyticsService,
} from './services/sustainability-dashboard-static-analytics.service';
import { DashboardApiResponse, DashboardContractEntry } from './sustainability-dashboard.models';

export enum SustainabilityTab {
  Home = 'home_',
  Water = 'water',
  Soil = 'soil',
  Climate = 'climate',
  Biodiversity = 'biodiversity',
  Livelihood = 'livelihood',
}

export const SUSTAINABILITY_TABS: { value: SustainabilityTab; label: string }[] = [
  { value: SustainabilityTab.Home, label: 'Home' },
  { value: SustainabilityTab.Water, label: 'Water' },
  { value: SustainabilityTab.Soil, label: 'Soil' },
  { value: SustainabilityTab.Climate, label: 'Climate' },
  { value: SustainabilityTab.Biodiversity, label: 'Biodiversity' },
  { value: SustainabilityTab.Livelihood, label: 'Livelihood' },
];

@Component({
  selector: 'app-sustainability-dashboard',
  imports: [SharedModule],
  templateUrl: './sustainability-dashboard-static.component.html',
  styleUrl: './sustainability-dashboard-static.component.less',
  providers: [SubcontractProxy, InputPriceProxy],
})
export class SustainabilityDashboardStaticComponent implements OnInit, OnDestroy {
  loading = false;
  get dataLoading$(): Observable<boolean> {
    return this._dashboardService.loading$;
  }
  contractOptions: { contractName: string; contractId: string }[] = [];
  selectedContractId: string | null = null;
  subContracts: SubcontractUserViewModel[] = [];
  selectedSubcontract: SubcontractUserViewModel;

  userEnterprises: UserEnterpriseInfoViewModel[] = [];
  selectedEnterprise: UserEnterpriseInfoViewModel | null = null;

  readonly SustainabilityTab = SustainabilityTab;
  selectedTab: SustainabilityTab = SustainabilityTab.Home;

  private destroy$ = new Subject<void>();
  private readonly DASHBOARD_BASE_ROUTE = '/sustainability-dashboard-s';
  private readonly ENTERPRISE_STORAGE_KEY = 'sustainability_selected_enterprise_id';
  private readonly subcontractProxy = inject(SubcontractProxy);
  private readonly _enterpriseProxy = inject(EnterpriseProxy);
  private readonly _csiService = inject(CsiService);
  private readonly _router = inject(Router);
  private readonly _dashboardService = inject(SustainabilityDashboardService);
  private readonly _analyticsService = inject(SustainabilityDashboardStaticAnalyticsService);
  private readonly _inputPriceProxy = inject(InputPriceProxy);

  tabOptions: { value: SustainabilityTab; label: string; show: boolean }[] = [
    { value: SustainabilityTab.Home, label: 'Home', show: true },
    { value: SustainabilityTab.Water, label: 'Water', show: true },
    { value: SustainabilityTab.Soil, label: 'Soil', show: true },
    { value: SustainabilityTab.Climate, label: 'Climate', show: true },
    { value: SustainabilityTab.Biodiversity, label: 'Biodiversity', show: true },
    { value: SustainabilityTab.Livelihood, label: 'Livelihood', show: true },
  ];

  ngOnInit(): void {
    this.loadDashboardData();
    this.syncSelectedTabFromRoute();
    this._router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.syncSelectedTabFromRoute());

    this.loadUserEnterprises();
  }

  private loadDashboardData(): void {
    if (this._dashboardService.hasData()) {
      this.restoreContractOptionsFromService();
      return;
    }
    this._dashboardService.setLoading(true);
    this._inputPriceProxy
      .inputPrice_GetDashboardJson()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this._dashboardService.setLoading(false))
      )
      .subscribe((res) => {
        const contracts = this.extractContracts(res?.result);
        this._dashboardService.setData(contracts);
        this.applyContracts(contracts);
      });
  }

  private applyContracts(contracts: DashboardContractEntry[]): void {
    this.contractOptions = contracts.map((c) => ({
      contractId: c.contract.id,
      contractName: c.contract.name,
    }));
    if (!this.selectedContractId && contracts.length) {
      this.selectedContractId = contracts[0].contract.id;
      this.onSelectContract();
    }
  }

  private restoreContractOptionsFromService(): void {
    this._dashboardService.contracts$.pipe(takeUntil(this.destroy$)).subscribe((contracts) => {
      if (contracts?.length) {
        this.applyContracts(contracts);
      }
    });
  }

  private extractContracts(payload: DashboardApiResponse | string | null | undefined): DashboardContractEntry[] {
    if (!payload) return [];

    const parsed: DashboardApiResponse = typeof payload === 'string' ? JSON.parse(payload) : payload;

    return Array.isArray(parsed) ? parsed : [];
  }

  onRefresh(): void {
    this._analyticsService.resetViewTracking();
    this.contractOptions = [];
    this.selectedContractId = null;
    this._dashboardService.reset();
    this.loadDashboardData();
  }

  private loadUserEnterprises(): void {
    this._enterpriseProxy.enterprise_GetUserEnterprises().subscribe((res) => {
      if (res.isSuccess && res.result?.length > 0) {
        this.userEnterprises = res.result;

        const storedId = localStorage.getItem(this.ENTERPRISE_STORAGE_KEY);
        const stored = storedId ? res.result.find((e) => e.enterpriseId === storedId) : null;
        this.onSelectEnterpriseChange(stored ?? res.result[0]);
      }
    });
  }

  onSelectEnterpriseChange(enterprise: UserEnterpriseInfoViewModel): void {
    this.selectedEnterprise = enterprise;
    localStorage.setItem(this.ENTERPRISE_STORAGE_KEY, enterprise.enterpriseId);
    this._csiService.selectEnterprise(enterprise);

    // TODO: remove this later
    const ENTERPRISE_ID_HIDE_WATER_TAB = '4de519dc-baff-48cf-b45d-5af583e620f1';
    const waterTab = this.tabOptions.find((t) => t.value === SustainabilityTab.Water);
    if (waterTab) {
      waterTab.show = enterprise.enterpriseId !== ENTERPRISE_ID_HIDE_WATER_TAB;
    }
    if (enterprise.enterpriseId === ENTERPRISE_ID_HIDE_WATER_TAB && this._router.url.includes(`${this.DASHBOARD_BASE_ROUTE}/water`)) {
      this._router.navigate([this.DASHBOARD_BASE_ROUTE, SustainabilityTab.Home]);
    }
  }

  onSelectContract(): void {
    if (this.selectedContractId) {
      const selected = this.contractOptions.find((c) => c.contractId === this.selectedContractId);
      this._dashboardService.selectContract(this.selectedContractId, selected?.contractName);
      this._analyticsService.trackTabViewed(this.selectedTab as SustainabilityDashboardTabRoute);
    }
    this.selectedSubcontract = this.selectedContractId
      ? this.subContracts.find((e) => e.contractId === this.selectedContractId)
      : null;
  }

  onSelectTab(tab: SustainabilityTab): void {
    this.selectedTab = tab;
    this._router.navigate([this.DASHBOARD_BASE_ROUTE, tab]);
  }

  private syncSelectedTabFromRoute(): void {
    const url = this._router.url;
    const tabMatch = url.match(/\/sustainability-dashboard-s\/(\w+)/);
    const path = tabMatch?.[1];
    if (path && Object.values(SustainabilityTab).includes(path as SustainabilityTab)) {
      this.selectedTab = path as SustainabilityTab;
      this._analyticsService.trackTabViewed(path as SustainabilityDashboardTabRoute);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
